import {getEmailGroupFileList, getEmailGroupFileListPage} from "@/api/sidebar";
import {ElMessage} from "element-plus";
import {nextTick} from "vue";
import {sortFolders} from "@/utils/util";

function normalizeUserId(userId) {
    if (userId === undefined || userId === null) {
        return ''
    }
    return String(userId).trim()
}

function isValidUserId(userId) {
    return !!userId && userId !== '0' && userId !== 'undefined' && userId !== 'null'
}

function extractErrorMessage(error) {
    if (typeof error === 'string') {
        return error
    }
    return error?.message || error?.data?.message || error?.response?.data?.message || ''
}

function shouldSilenceUserIdError(message) {
    const normalizedMessage = String(message ?? '').replace(/\s+/g, '').toLowerCase()
    return normalizedMessage.includes('用户id不能为空') || normalizedMessage.includes('userid不能为空')
}

const sidebar = {
    namespaced: true,
    state: {
        fileList: [],
        targetFileList: [],
        isRouterAlive: true,
        reloadToken: 0,
        latestFileListRequestId: 0,
        latestAllListRequestId: 0,
        pageSetting: {
            pageNo: 1,
            pageSize: 20,
            total: 0,
            pages: 0,
        },
    },
    getters: {
        hasMoreData(state) {
            const pages = Number(state.pageSetting.pages || 0)
            const pageNo = Number(state.pageSetting.pageNo || 0)
            if (Number.isFinite(pages) && pages > 0) {
                return pageNo < pages
            }

            const total = Number(state.pageSetting.total || 0)
            if (Number.isFinite(total) && total > 0) {
                return state.targetFileList.length < total
            }

            return true
        }
    },
    mutations: {
        reload(state) {
            state.reloadToken += 1
            const currentReloadToken = state.reloadToken
            state.isRouterAlive = false
            nextTick(() => {
                if (currentReloadToken !== state.reloadToken) {
                    return
                }
                state.isRouterAlive = true
            })
        },
        setRouterAlive(state, payload) {
            state.isRouterAlive = !!payload
        },
        bumpLatestFileListRequestId(state) {
            state.latestFileListRequestId += 1
        },
        bumpLatestAllListRequestId(state) {
            state.latestAllListRequestId += 1
        },
        clearPageData(state) {
            state.targetFileList = []
            state.pageSetting.total = 0
            state.pageSetting.pages = 0
        },
        setFileList(state, payload) {
            state.fileList = payload
        },
        setPageData(state, payload) {
            state.pageSetting.pageNo = payload.current
            state.pageSetting.pageSize = payload.size
            state.pageSetting.total = payload.total
            state.pageSetting.pages = payload.pages
        },
        setPagedTargetFileList(state, payload) {
            const {isUpdateFileList, records} = payload
            if (isUpdateFileList) {
                state.targetFileList = [...state.targetFileList, ...records]
            } else {
                state.targetFileList = records
            }
        },
        setTargetFileList(state, payload) {
            state.targetFileList = payload
        },
        setPageSetting(state, payload) {
            const {type, value} = payload
            state.pageSetting[type] = value
        }
    },
    actions: {
        // 分页查询
        async getEmailGroupFileListApis({state, commit}, payload) {
            const {isUpdateFileList, teamId, userId} = payload
            const normalizedUserId = normalizeUserId(userId)

            if (!isValidUserId(normalizedUserId)) {
                console.warn('getEmailGroupFileListApis: userId is invalid, skipping API call')
                if (!isUpdateFileList) {
                    commit('clearPageData')
                }
                return
            }

            commit('bumpLatestFileListRequestId')
            const requestId = state.latestFileListRequestId

            let res
            try {
                res = await getEmailGroupFileListPage({
                    teamId,
                    userId: normalizedUserId,
                    pageNo: state.pageSetting.pageNo,
                    pageSize: state.pageSetting.pageSize
                })
            } catch (error) {
                const message = extractErrorMessage(error)
                if (message) {
                    console.warn('getEmailGroupFileListApis error:', message)
                }
                return
            }

            if (requestId !== state.latestFileListRequestId) {
                return
            }

            if (res.code !== 200) {
                if (res.__silencedBusinessFail || shouldSilenceUserIdError(res.message)) {
                    return
                }
                ElMessage.error(res.message)
                return
            }

            commit('setPageData', res.result)
            commit('setPagedTargetFileList', {isUpdateFileList, records: res.result.records})
        },
        async getEmailGroupFileAllList({state, commit}, payload) {
            const {teamId, userId} = payload
            const normalizedUserId = normalizeUserId(userId)

            if (!isValidUserId(normalizedUserId)) {
                console.warn('getEmailGroupFileAllList: userId is invalid, skipping API call')
                return
            }

            commit('bumpLatestAllListRequestId')
            const requestId = state.latestAllListRequestId

            let res
            try {
                res = await getEmailGroupFileList({
                    teamId,
                    userId: normalizedUserId,
                })
            } catch (error) {
                const message = extractErrorMessage(error)
                if (message) {
                    console.warn('getEmailGroupFileAllList error:', message)
                }
                return
            }

            if (requestId !== state.latestAllListRequestId) {
                return
            }

            if (res.code !== 200) {
                if (res.__silencedBusinessFail || shouldSilenceUserIdError(res.message)) {
                    return
                }
                ElMessage.error(res.message)
                return
            }

            const result = sortFolders(res.result)
            commit('setFileList', result)
        }
    },
}

export default sidebar
